import React, { useEffect, useState } from 'react';
import { X, Link2, Loader2, Check, AlertTriangle, RefreshCw, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useLanguage } from '../context/LanguageContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ALLMX_CATEGORIES = [
  'Abbigliamento', 'Adesivi', 'Filtri aria', 'Freni', 'Leve', 'Lubrificanti',
  'Parti elettriche', 'Parti motore', 'Plastiche', 'Pneumatici', 'Protezioni moto',
  'Ruote', 'Sospensioni', 'Trasmissione', 'Altro',
];

/**
 * ImportFromUrlModal — two-step wizard:
 *   1. Admin pastes an eBay URL → scrape → pre-fill form
 *   2. Admin reviews/edits all fields → save
 */
const ImportFromUrlModal = ({ onClose, onSaved }) => {
  const { language } = useLanguage();
  const t = (it, en, es) => (language === 'it' ? it : language === 'es' ? es : en);

  const [step, setStep] = useState(1); // 1 = URL input, 2 = review form
  const [url, setUrl] = useState('');
  const [scraping, setScraping] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(null);
  const [selectedImages, setSelectedImages] = useState({});
  const [duplicate, setDuplicate] = useState(null); // { sku, slug, name_it } | null

  // Live check duplicate SKU (debounced)
  useEffect(() => {
    if (!form?.sku) { setDuplicate(null); return; }
    const sku = form.sku.trim();
    if (!sku) { setDuplicate(null); return; }
    const handle = setTimeout(async () => {
      try {
        const { data } = await axios.post(
          `${API_URL}/api/admin/check-skus`,
          { skus: [sku] },
          { headers: { 'Content-Type': 'application/json' } }
        );
        const found = (data?.existing || [])[0];
        setDuplicate(found || null);
      } catch (_) {
        setDuplicate(null);
      }
    }, 400);
    return () => clearTimeout(handle);
  }, [form?.sku]);

  const handleScrape = async () => {
    const cleanUrl = url.trim();
    if (!cleanUrl) {
      toast.error(t('Inserisci un URL eBay', 'Enter an eBay URL', 'Introduce una URL de eBay'));
      return;
    }
    if (!/ebay\.(com|it|co\.uk|de|fr|es|ie|ca|com\.au|at|be|nl|ch)/i.test(cleanUrl)) {
      toast.error(t('Solo URL eBay sono supportati', 'Only eBay URLs are supported', 'Solo URLs de eBay son compatibles'));
      return;
    }
    setScraping(true);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/admin/scrape-ebay`,
        { url: cleanUrl },
        { headers: { 'Content-Type': 'application/json' }, timeout: 30000 }
      );

      setForm({
        sku: data.suggested_sku || '',
        name_it: data.name_it || '',
        name_en: data.name_en || '',
        description_it: data.description_it || '',
        description_en: data.description_en || '',
        price: data.price != null ? String(data.price) : '',
        stock: data.stock != null ? String(data.stock) : '1',
        brand: data.brand || '',
        category: data.category || 'Altro',
        weight: '0.5',
        images: data.images || [],
        ebay_item_id: data.ebay_item_id || null,
        source_url: data.source_url || cleanUrl,
      });
      // Select all scraped images by default
      const sel = {};
      (data.images || []).forEach((img) => { sel[img] = true; });
      setSelectedImages(sel);
      setStep(2);
      toast.success(t('Dati estratti con successo!', 'Data extracted successfully!', '¡Datos extraídos con éxito!'));
    } catch (e) {
      const msg = e.response?.data?.detail || e.message || t('Errore durante lo scraping', 'Scraping error', 'Error de scraping');
      toast.error(msg);
    } finally {
      setScraping(false);
    }
  };

  const handleSave = async () => {
    if (!form.sku.trim()) {
      toast.error(t('SKU obbligatorio', 'SKU required', 'SKU obligatorio'));
      return;
    }
    if (duplicate) {
      toast.error(
        t(
          `SKU ${duplicate.sku} già esistente — cambialo prima di salvare`,
          `SKU ${duplicate.sku} already exists — change it before saving`,
          `SKU ${duplicate.sku} ya existe — cámbialo antes de guardar`,
        ),
      );
      return;
    }
    if (!form.name_it.trim()) {
      toast.error(t('Nome italiano obbligatorio', 'Italian name required', 'Nombre italiano obligatorio'));
      return;
    }
    const priceNum = parseFloat(form.price);
    if (isNaN(priceNum) || priceNum < 0) {
      toast.error(t('Prezzo non valido', 'Invalid price', 'Precio no válido'));
      return;
    }
    const chosenImages = form.images.filter((u) => selectedImages[u]);

    setSaving(true);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/admin/create-product`,
        {
          sku: form.sku.trim(),
          name_it: form.name_it.trim(),
          name_en: (form.name_en || form.name_it).trim(),
          description_it: form.description_it || '',
          description_en: form.description_en || form.description_it || '',
          price: priceNum,
          stock: parseInt(form.stock || '0', 10),
          brand: form.brand || null,
          category: form.category,
          weight: parseFloat(form.weight || '0.5'),
          images: chosenImages,
          ebay_item_id: form.ebay_item_id,
          source_url: form.source_url,
        },
        { headers: { 'Content-Type': 'application/json' }, timeout: 20000 }
      );
      toast.success(t('Prodotto salvato!', 'Product saved!', '¡Producto guardado!'));
      onSaved && onSaved(data.product);
      onClose();
    } catch (e) {
      const msg = e.response?.data?.detail || e.message || t('Errore nel salvataggio', 'Save error', 'Error al guardar');
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
      <div
        className="relative w-full max-w-3xl max-h-[90vh] overflow-y-auto border"
        style={{ background: '#0f0f0f', borderColor: '#2a2a2a' }}
        data-testid="import-url-modal"
      >
        {/* Header */}
        <div className="sticky top-0 flex items-center justify-between px-6 py-4 border-b z-10"
          style={{ background: '#0f0f0f', borderColor: '#2a2a2a' }}>
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <Link2 size={22} className="text-[#FF6600]" />
            {step === 1
              ? t('Importa prodotto da URL eBay', 'Import product from eBay URL', 'Importar producto desde URL de eBay')
              : t('Rivedi dati prodotto', 'Review product data', 'Revisar datos del producto')}
          </h2>
          <button onClick={onClose} className="text-white/60 hover:text-white" data-testid="close-modal">
            <X size={24} />
          </button>
        </div>

        {/* STEP 1 — URL input */}
        {step === 1 && (
          <div className="p-6 space-y-4">
            <div className="bg-[#FF6600]/10 border-l-4 border-[#FF6600] p-4 text-sm text-white/90">
              <p className="mb-1 font-semibold">
                {t('Come funziona', 'How it works', 'Cómo funciona')}
              </p>
              <ol className="list-decimal ml-5 space-y-1 text-white/70">
                <li>{t('Incolla il link di un prodotto eBay', 'Paste an eBay product link', 'Pega un enlace de producto de eBay')}</li>
                <li>{t('Clicca "Estrai dati" — il sistema compila il form', 'Click "Extract data" — the system fills the form', 'Haz clic en "Extraer datos" — el sistema llenará el formulario')}</li>
                <li>{t('Rivedi, modifica i campi e clicca "Salva"', 'Review, edit the fields and click "Save"', 'Revisa, edita los campos y haz clic en "Guardar"')}</li>
              </ol>
            </div>

            <label className="block text-sm font-semibold text-white">
              {t('URL eBay', 'eBay URL', 'URL de eBay')}
            </label>
            <input
              type="url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://www.ebay.it/itm/..."
              className="w-full px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-[#FF6600]"
              style={{ background: '#1a1a1a', border: '1px solid #2a2a2a' }}
              data-testid="import-url-input"
              onKeyDown={(e) => e.key === 'Enter' && handleScrape()}
            />

            <button
              onClick={handleScrape}
              disabled={scraping || !url.trim()}
              className="w-full flex items-center justify-center gap-2 bg-[#FF6600] text-black font-bold py-3 px-6 hover:bg-[#ff8833] transition-colors disabled:opacity-50"
              data-testid="scrape-btn"
            >
              {scraping ? <Loader2 size={20} className="animate-spin" /> : <RefreshCw size={20} />}
              {scraping
                ? t('Estrazione in corso...', 'Extracting...', 'Extrayendo...')
                : t('Estrai dati dal link', 'Extract data from link', 'Extraer datos del enlace')}
            </button>

            <div className="text-xs text-white/50 flex items-start gap-2 mt-4">
              <AlertTriangle size={14} className="mt-0.5 flex-shrink-0 text-yellow-500" />
              <span>
                {t(
                  'Funziona con link di item eBay (es. ebay.it/itm/...). Alcuni prodotti potrebbero avere dati parziali che richiedono correzione manuale.',
                  'Works with eBay item links (e.g. ebay.it/itm/...). Some products may have partial data requiring manual edits.',
                  'Funciona con enlaces de artículos de eBay. Algunos productos pueden requerir ediciones manuales.'
                )}
              </span>
            </div>
          </div>
        )}

        {/* STEP 2 — Review / edit form */}
        {step === 2 && form && (
          <div className="p-6 space-y-5">
            <button
              onClick={() => setStep(1)}
              className="text-sm text-white/60 hover:text-white underline"
              data-testid="back-to-step1"
            >
              ← {t('Cambia URL', 'Change URL', 'Cambiar URL')}
            </button>

            {duplicate && (
              <div
                className="p-4 border-l-4 border-red-500 bg-red-500/10 text-white/90 flex items-start gap-3"
                data-testid="ebay-duplicate-warning"
              >
                <AlertTriangle size={20} className="text-red-500 flex-shrink-0 mt-0.5" />
                <div className="flex-1 text-sm">
                  <div className="font-bold text-red-400 mb-1">
                    {t('SKU già presente sul sito', 'SKU already in catalog', 'SKU ya está en el catálogo')}
                  </div>
                  <div>
                    {t('Esiste già un prodotto con SKU', 'A product already exists with SKU', 'Ya existe un producto con SKU')}{' '}
                    <span className="font-mono text-white">{duplicate.sku}</span>:{' '}
                    <span className="text-white">{duplicate.name_it}</span>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-3 text-xs">
                    <a
                      href={`/products/${duplicate.slug || duplicate.sku}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-[#FF6600] hover:underline"
                    >
                      <ExternalLink size={12} /> {t('Vedi prodotto esistente', 'View existing product', 'Ver producto existente')}
                    </a>
                    <span className="text-white/50">
                      {t('Cambia lo SKU per creare un nuovo prodotto.', 'Change the SKU to create a new product.', 'Cambia el SKU para crear un nuevo producto.')}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* SKU + Brand */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field
                label={t('Codice prodotto (SKU) *', 'Product Code (SKU) *', 'Código producto (SKU) *')}
                value={form.sku}
                onChange={(v) => setForm({ ...form, sku: v })}
                testid="form-sku"
              />
              <Field
                label={t('Marca', 'Brand', 'Marca')}
                value={form.brand}
                onChange={(v) => setForm({ ...form, brand: v })}
                testid="form-brand"
              />
            </div>

            {/* Name IT + EN */}
            <Field
              label={t('Nome (Italiano) *', 'Name (Italian) *', 'Nombre (Italiano) *')}
              value={form.name_it}
              onChange={(v) => setForm({ ...form, name_it: v })}
              testid="form-name-it"
            />
            <Field
              label={t('Nome (Inglese)', 'Name (English)', 'Nombre (Inglés)')}
              value={form.name_en}
              onChange={(v) => setForm({ ...form, name_en: v })}
              testid="form-name-en"
            />

            {/* Description IT + EN */}
            <Field
              label={t('Descrizione (Italiano)', 'Description (Italian)', 'Descripción (Italiano)')}
              value={form.description_it}
              onChange={(v) => setForm({ ...form, description_it: v })}
              textarea
              testid="form-desc-it"
            />
            <Field
              label={t('Descrizione (Inglese)', 'Description (English)', 'Descripción (Inglés)')}
              value={form.description_en}
              onChange={(v) => setForm({ ...form, description_en: v })}
              textarea
              testid="form-desc-en"
            />

            {/* Price / Stock / Weight */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <Field
                label={t('Prezzo (€) *', 'Price (€) *', 'Precio (€) *')}
                value={form.price}
                onChange={(v) => setForm({ ...form, price: v })}
                type="number"
                testid="form-price"
              />
              <Field
                label={t('Stock', 'Stock', 'Stock')}
                value={form.stock}
                onChange={(v) => setForm({ ...form, stock: v })}
                type="number"
                testid="form-stock"
              />
              <Field
                label={t('Peso (kg)', 'Weight (kg)', 'Peso (kg)')}
                value={form.weight}
                onChange={(v) => setForm({ ...form, weight: v })}
                type="number"
                testid="form-weight"
              />
            </div>

            {/* Category */}
            <div>
              <label className="block text-sm font-semibold text-white mb-1">
                {t('Categoria *', 'Category *', 'Categoría *')}
              </label>
              <select
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                className="w-full px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-[#FF6600]"
                style={{ background: '#1a1a1a', border: '1px solid #2a2a2a' }}
                data-testid="form-category"
              >
                {ALLMX_CATEGORIES.map((c) => (
                  <option key={c} value={c} style={{ background: '#1a1a1a' }}>{c}</option>
                ))}
              </select>
            </div>

            {/* Images gallery */}
            <div>
              <label className="block text-sm font-semibold text-white mb-2">
                {t(`Immagini (${form.images.length} trovate, clicca per selezionare)`,
                   `Images (${form.images.length} found, click to select)`,
                   `Imágenes (${form.images.length} encontradas, clic para seleccionar)`)}
              </label>
              {form.images.length === 0 ? (
                <p className="text-sm text-white/50 italic">
                  {t('Nessuna immagine trovata', 'No images found', 'No se encontraron imágenes')}
                </p>
              ) : (
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2">
                  {form.images.map((img) => (
                    <button
                      key={img}
                      type="button"
                      onClick={() => setSelectedImages((s) => ({ ...s, [img]: !s[img] }))}
                      className="relative aspect-square border-2 transition-colors overflow-hidden"
                      style={{
                        borderColor: selectedImages[img] ? '#FF6600' : '#2a2a2a',
                        background: '#1a1a1a',
                      }}
                      data-testid="toggle-image"
                    >
                      <img src={img} alt="" className="w-full h-full object-cover" />
                      {selectedImages[img] && (
                        <div className="absolute top-1 right-1 bg-[#FF6600] rounded-full p-0.5">
                          <Check size={14} color="black" strokeWidth={3} />
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Source info (read-only) */}
            {form.source_url && (
              <div className="text-xs text-white/40 truncate">
                {t('Origine', 'Source', 'Fuente')}:{' '}
                <a href={form.source_url} target="_blank" rel="noopener noreferrer" className="text-[#FF6600] hover:underline">
                  {form.source_url}
                </a>
              </div>
            )}

            {/* Save button */}
            <button
              onClick={handleSave}
              disabled={saving}
              className="w-full flex items-center justify-center gap-2 bg-[#FF6600] text-black font-bold py-3 px-6 hover:bg-[#ff8833] transition-colors disabled:opacity-50"
              data-testid="save-product-btn"
            >
              {saving ? <Loader2 size={20} className="animate-spin" /> : <Check size={20} />}
              {saving
                ? t('Salvataggio...', 'Saving...', 'Guardando...')
                : t('Salva prodotto', 'Save product', 'Guardar producto')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const Field = ({ label, value, onChange, type = 'text', textarea = false, testid }) => (
  <div>
    <label className="block text-sm font-semibold text-white mb-1">{label}</label>
    {textarea ? (
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={3}
        className="w-full px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-[#FF6600] resize-y"
        style={{ background: '#1a1a1a', border: '1px solid #2a2a2a' }}
        data-testid={testid}
      />
    ) : (
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-[#FF6600]"
        style={{ background: '#1a1a1a', border: '1px solid #2a2a2a' }}
        data-testid={testid}
      />
    )}
  </div>
);

export default ImportFromUrlModal;
